import * as preact from "preact";
import * as _ from "underscore";

import * as accountModal from "accountModal";
import UserWordModel from "models/UserWordModel";
import models from "models/models";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import { cn } from "shared/utils";
import AskAIView from "views/AskAIView";
import EditableListView from "views/EditableListView";
import ExplanationView from "views/ExplanationView";

interface Props {
  userWord: UserWordModel;
  options: {
    title?: string;
    newCard?: boolean;
    doneButton?: boolean;
  };
  phrase: boolean;

  doneCallback?: () => void;
  savedCallback?: () => void;
}

type Explanation =
  | { type: "fetching" }
  | { type: "fetched"; explanation: string }
  | { type: "not-found" }
  | { type: "generating" }
  | { type: "reached-limit" };

interface UserWordEdit {
  word: string;
  alternativeWords: string[];

  translation: string;
  alternativeTranslations: string[];

  contexts: {
    preactKey: string;
    _id?: string;
    text: string;
    bookID?: {
      _id: string;
      title: string;
    };

    URL?: string;

    // TODO: should these be moved out of here?
    valid?: boolean;
    generate?: boolean;
  }[];

  imageURL?: string;
  note?: string;
  passiveMode: boolean;
}

interface State {
  editedUserWord: UserWordEdit;
  initialUserWord: UserWordEdit;

  dictionaryVisible: boolean;
}

/** This persists across instances */
let globalDictionaryVisible = false;

const initializeEditedUserWord = (
  userWord: UserWordModel
): State["editedUserWord"] =>
  validateContexts({
    word: userWord.get("word"),
    alternativeWords: userWord.get("alternativeWords"),
    translation: userWord.get("translation"),
    alternativeTranslations: userWord.get("alternativeTranslations"),
    contexts: userWord.get("contexts").map((context) => ({
      ...context,
      preactKey: Math.random().toString(),
    })),
    imageURL: userWord.get("imageURL"),
    note: userWord.get("note"),
    passiveMode: userWord.get("passiveMode"),
  });

const validateContexts = (
  editedUserWord: State["editedUserWord"]
): State["editedUserWord"] => {
  const newEditedUserWord = utils.deepClone(editedUserWord);
  const { word, contexts } = editedUserWord;

  for (let index = 0; index < contexts.length; index++) {
    const context = contexts[index];

    newEditedUserWord.contexts[index].valid =
      word.trim() !== "" &&
      new RegExp("(\\W|^)(" + utils.regexpEscape(word) + ")(\\W|$)", "i").test(
        context.text
      );
  }

  return newEditedUserWord;
};

class EditCardView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    models.user.on("change:firstLanguage", this.onUserChange);

    const initialUserWord = initializeEditedUserWord(props.userWord);

    this.state = {
      editedUserWord: initialUserWord,
      initialUserWord,
      dictionaryVisible: globalDictionaryVisible,
    };
  }

  onUserChange = () => {
    this.forceUpdate();
  };

  componentWillUnmount() {
    models.user.off("change:firstLanguage", this.onUserChange);
  }

  componentWillUpdate(
    _nextProps: Readonly<Props>,
    nextState: Readonly<State>
  ): void {
    if (
      !this.props.options.newCard &&
      nextState.editedUserWord !== this.state.editedUserWord
    ) {
      this.debouncedSave();
    }
  }

  debouncedSave = _.debounce(async () => {
    this.saveChanges();
  }, 400);

  render() {
    const { userWord, options, phrase } = this.props;
    const editedUserWord = this.state.editedUserWord;

    const allValid = editedUserWord.contexts.every((context) => context.valid);

    const embeddedDictionary = languageMap.getEmbeddedDictionary(
      userWord.get("language"),
      models.user.get("readerTranslations") === "off"
        ? userWord.get("language")
        : models.user.get("firstLanguage"),
      userWord.get("word"),
      // XXX populate context
      undefined,
      models.user
    );

    const languageString = languageMap.map[userWord.get("language")].english;
    const firstLanguageString =
      languageMap.map[models.user.get("firstLanguage")].english;

    const showInFirstLanguagePreference =
      models.user.isSmartDictionaryInFirstLanguage(userWord.get("language"));

    const addFieldsButtons =
      editedUserWord.note === undefined ||
      editedUserWord.imageURL === undefined ? (
        <>
          {editedUserWord.note === undefined ? (
            <button
              class="linkButton"
              onClick={async () => {
                await this.setState({
                  editedUserWord: {
                    ...editedUserWord,
                    note: "",
                  },
                });
              }}
            >
              <i class="icon icon-plus"></i> Add Notes
            </button>
          ) : null}
          {editedUserWord.imageURL === undefined ? (
            <button
              class="linkButton imageURL"
              onClick={() =>
                this.setState({
                  editedUserWord: {
                    ...editedUserWord,
                    imageURL: "",
                  },
                })
              }
            >
              <i class="icon icon-plus"></i> Add Image URL
            </button>
          ) : null}
        </>
      ) : null;

    return (
      <div className="editCardPanel">
        <h2 className="spacing first">{options.title}</h2>

        <table
          class={cn({
            editCardTable: true,
            noSynonyms: this.state.editedUserWord.alternativeWords.length === 0,
          })}
        >
          <tbody>
            <tr class="editWordRow">
              <td>{languageString}</td>
              <td>
                <input
                  autoCapitalize="off"
                  type="text"
                  name="word"
                  value={editedUserWord.word}
                  onInput={(event) =>
                    this.setState({
                      editedUserWord: validateContexts({
                        ...editedUserWord,
                        word: event.currentTarget.value,
                        contexts: editedUserWord.contexts.map((context) => ({
                          ...context,
                          generate: false,
                        })),
                      }),
                    })
                  }
                  class={allValid ? null : "invalid"}
                />
              </td>
            </tr>
            <tr class="synonymsRow">
              <td>
                {this.state.editedUserWord.alternativeWords.length > 0
                  ? `Synonym${
                      this.state.editedUserWord.alternativeWords.length > 1
                        ? "s"
                        : ""
                    }`
                  : null}
              </td>
              <td>
                <EditableListView
                  items={this.state.editedUserWord.alternativeWords}
                  addButtonText={`Add ${languageMap.map[userWord.get("language")].english} Synonym`}
                  onChange={(items) =>
                    this.setState({
                      editedUserWord: {
                        ...editedUserWord,
                        alternativeWords: items,
                      },
                    })
                  }
                  language={userWord.get("language")}
                />
              </td>
            </tr>
            <tr>
              <td>
                Translation
                {editedUserWord.alternativeTranslations.length > 0 ? "s" : ""}
                <br />
                <small>({firstLanguageString})</small>
              </td>
              <td>
                <EditableListView
                  autoFocus={true}
                  items={[
                    this.state.editedUserWord.translation,
                    ...this.state.editedUserWord.alternativeTranslations,
                  ]}
                  onChange={(items) =>
                    this.setState({
                      editedUserWord: {
                        ...editedUserWord,
                        translation: items[0],
                        alternativeTranslations: items.slice(1),
                      },
                    })
                  }
                  addButtonText="Add Translation"
                  language={models.user.get("firstLanguage")}
                  preventEmptyList={true}
                />
              </td>
            </tr>
            <tr id="contextThrobber" style={{ display: "none" }}>
              <td></td>
              <td>
                <img src="/content/throbber.gif" />
              </td>
            </tr>
            {editedUserWord.contexts.map((context, index) => {
              const onClickGenerate = () => {
                this.setState({
                  editedUserWord: validateContexts({
                    ...editedUserWord,
                    contexts: [
                      ...editedUserWord.contexts.slice(0, index),
                      {
                        ...editedUserWord.contexts[index],
                        generate: true,
                      },
                      ...editedUserWord.contexts.slice(index + 1),
                    ],
                  }),
                });
              };

              return (
                <tr
                  class={cn({
                    contextRow: true,
                    invalidContext: !context.valid,
                  })}
                  key={`context-${context.preactKey}`}
                >
                  <td>
                    {index === 0
                      ? `Context${editedUserWord.contexts.length > 1 ? "s" : ""}`
                      : null}
                  </td>
                  <td>
                    <div class="contextContainer">
                      <div class="contextContainerInner">
                        <textarea
                          type="text"
                          name="context"
                          rows={6}
                          placeholder="Enter example context here"
                          onInput={(event) => {
                            this.setState({
                              editedUserWord: validateContexts({
                                ...editedUserWord,
                                contexts: [
                                  ...editedUserWord.contexts.slice(0, index),
                                  {
                                    ...editedUserWord.contexts[index],
                                    text: event.currentTarget.value,
                                    generate: false,
                                  },
                                  ...editedUserWord.contexts.slice(index + 1),
                                ],
                              }),
                            });
                          }}
                        >
                          {context.text}
                        </textarea>
                        {context.bookID?._id || context.URL ? (
                          <div class="contextLinkContainer">
                            <h3>Source</h3>
                            <a
                              class="contextLink"
                              href={
                                context.bookID?._id
                                  ? `/library/${context.bookID?._id}`
                                  : context.URL
                              }
                            >
                              {context.bookID?.title ??
                                utils.elide(
                                  utils.getDomainFromUrl(context.URL),
                                  40
                                )}
                            </a>
                          </div>
                        ) : null}
                      </div>
                      <button
                        class="linkButton removeContext"
                        onClick={() => {
                          this.setState({
                            editedUserWord: validateContexts({
                              ...editedUserWord,
                              contexts: [
                                ...editedUserWord.contexts.slice(0, index),
                                ...editedUserWord.contexts.slice(index + 1),
                              ],
                            }),
                          });
                        }}
                      >
                        <i class="icon icon-remove-sign"></i>
                      </button>
                    </div>
                    <ExplanationView
                      word={editedUserWord.word}
                      context={context.text}
                      language={userWord.get("language")}
                      showInFirstLanguage={false}
                      existingTranslations={[
                        editedUserWord.translation,
                        ...editedUserWord.alternativeTranslations,
                      ]}
                      onSelectTranslation={(translation) =>
                        this.setState({
                          editedUserWord: {
                            ...editedUserWord,
                            alternativeTranslations: [
                              ...editedUserWord.alternativeTranslations,
                              translation,
                            ],
                          },
                        })
                      }
                      cacheOnly={
                        !(!showInFirstLanguagePreference && context.generate)
                      }
                      showExplanationButton={!showInFirstLanguagePreference}
                      onClickGenerate={onClickGenerate}
                    />
                    <ExplanationView
                      word={editedUserWord.word}
                      context={context.text}
                      language={userWord.get("language")}
                      showInFirstLanguage={true}
                      existingTranslations={[
                        editedUserWord.translation,
                        ...editedUserWord.alternativeTranslations,
                      ]}
                      onSelectTranslation={(translation) =>
                        this.setState({
                          editedUserWord: {
                            ...editedUserWord,
                            alternativeTranslations: [
                              ...editedUserWord.alternativeTranslations,
                              translation,
                            ],
                          },
                        })
                      }
                      cacheOnly={
                        !(showInFirstLanguagePreference && context.generate)
                      }
                      showExplanationButton={showInFirstLanguagePreference}
                      onClickGenerate={onClickGenerate}
                    />
                  </td>
                </tr>
              );
            })}
            <tr class="addContextRow">
              <td></td>
              <td>
                <button
                  class="linkButton"
                  onClick={() =>
                    this.setState({
                      editedUserWord: {
                        ...editedUserWord,
                        contexts: [
                          ...editedUserWord.contexts,
                          {
                            preactKey: Math.random().toString(),
                            text: "",
                          },
                        ],
                      },
                    })
                  }
                >
                  <i class="icon icon-plus"></i> Add Context
                </button>
                {editedUserWord.note === undefined &&
                editedUserWord.imageURL === undefined
                  ? addFieldsButtons
                  : null}
              </td>
            </tr>

            {editedUserWord.note !== undefined ? (
              <tr>
                <td>Notes</td>
                <td>
                  <div class="contextContainer">
                    <textarea
                      className="note"
                      placeholder="Type notes here"
                      value={editedUserWord.note}
                      onInput={(event) =>
                        this.setState({
                          editedUserWord: {
                            ...editedUserWord,
                            note: event.currentTarget.value,
                          },
                        })
                      }
                    />
                    <button
                      class="linkButton removeContext"
                      onClick={() => {
                        this.setState({
                          editedUserWord: validateContexts({
                            ...editedUserWord,
                            note: undefined,
                          }),
                        });
                      }}
                    >
                      <i class="icon icon-remove-sign"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ) : null}
            {editedUserWord.imageURL !== undefined ? (
              <tr className="imageURL">
                <td>Image URL</td>
                <td>
                  <img
                    className="image"
                    src={editedUserWord.imageURL}
                    style={{
                      display: editedUserWord.imageURL ? "block" : "none",
                    }}
                  />
                  <div class="contextContainer">
                    <input
                      className="imageURL"
                      type="url"
                      title='Find an image anywhere on the web, right click it and select "Copy Image Location" or "Copy Image URL" and then paste it into this box.'
                      placeholder="Paste Image URL here"
                      value={editedUserWord.imageURL}
                      onInput={(event) =>
                        this.setState({
                          editedUserWord: {
                            ...editedUserWord,
                            imageURL: event.currentTarget.value,
                          },
                        })
                      }
                    />
                    <button
                      class="linkButton removeContext"
                      onClick={() => {
                        this.setState({
                          editedUserWord: validateContexts({
                            ...editedUserWord,
                            imageURL: undefined,
                          }),
                        });
                      }}
                    >
                      <i class="icon icon-remove-sign"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ) : null}
            {editedUserWord.passiveMode && (
              <tr>
                <td>Passive Mode</td>
                <td>
                  <label>
                    <input
                      className="passiveModeCheckbox"
                      type="checkbox"
                      checked={editedUserWord.passiveMode}
                    />{" "}
                    (if checked, typing exercises in new practice are disabled)
                  </label>
                </td>
              </tr>
            )}
            {(editedUserWord.note !== undefined ||
              editedUserWord.imageURL !== undefined) &&
            addFieldsButtons ? (
              <tr>
                <td></td>
                <td>{addFieldsButtons}</td>
              </tr>
            ) : null}
            {models.user.hasFeature("askAI") ? (
              <tr>
                <td></td>
                <td>
                  <AskAIView
                    userWord={this.props.userWord}
                    context={this.props.userWord
                      .get("contexts")
                      .map((context) => context.text)
                      .join("... ")}
                  />
                </td>
              </tr>
            ) : null}
            <tr>
              <td colSpan={2} className="buttonRow">
                {embeddedDictionary && (
                  <>
                    <button
                      className="showDictionaryButton prominent large noMobile"
                      onClick={async () => {
                        await this.setState({
                          dictionaryVisible: !this.state.dictionaryVisible,
                        });
                        globalDictionaryVisible = this.state.dictionaryVisible;
                      }}
                    >
                      {this.state.dictionaryVisible ? "Hide" : "Show"}{" "}
                      Dictionary
                    </button>
                    <button
                      className="openDictionaryButton prominent large mobileOnly"
                      onClick={() =>
                        window.open(embeddedDictionary.URL, "_blank")
                      }
                    >
                      Open Dictionary
                    </button>
                  </>
                )}
                {!options.newCard ? (
                  <button
                    className="undoButton prominent large noMobile"
                    onClick={() => {
                      this.setState({
                        editedUserWord: this.state.initialUserWord,
                      });
                    }}
                    disabled={_.isEqual(
                      this.state.editedUserWord,
                      this.state.initialUserWord
                    )}
                  >
                    Undo Changes
                  </button>
                ) : null}
                {options.newCard ? (
                  <button
                    className="save prominent large"
                    onClick={async () => {
                      if (await this.saveChanges()) {
                        this.props.doneCallback?.();
                      }
                    }}
                  >
                    Add {phrase ? "Phrase" : "Word"}
                  </button>
                ) : null}
                {options.doneButton ? (
                  <button
                    className="save prominent large"
                    onClick={async () => {
                      this.props.doneCallback();
                    }}
                  >
                    Done
                  </button>
                ) : null}
              </td>
            </tr>
          </tbody>
        </table>
        {embeddedDictionary ? (
          this.state.dictionaryVisible ? (
            <div
              class="embeddedDictionary"
              dangerouslySetInnerHTML={{
                __html: embeddedDictionary.html,
              }}
            ></div>
          ) : null
        ) : (
          <>
            <br />
            Sorry, no dictionary definitions available between {
              languageString
            }{" "}
            and {firstLanguageString}.
          </>
        )}
      </div>
    );
  }

  async saveChanges() {
    const editedUserWord = this.state.editedUserWord;

    if (
      this.props.options.newCard &&
      (editedUserWord.word.trim() === "" ||
        editedUserWord.translation.trim() === "")
    ) {
      alert("You must include a word and its translation");
      return false;
    }

    if (
      this.props.options.newCard &&
      editedUserWord.contexts.some((context) => !context.valid) &&
      !confirm(
        `Your context doesn't include the word "${editedUserWord.word}".

Are you sure you want to save?`
      )
    ) {
      return false;
    }

    const userWordToSave: any = {
      ...editedUserWord,
      word: editedUserWord.word.trim(),
      alternativeWords: editedUserWord.alternativeWords
        .map((word) => word.trim())
        .filter((word) => word !== ""),
      translation: editedUserWord.translation.trim(),
      alternativeTranslations: editedUserWord.alternativeTranslations
        .map((word) => word.trim())
        .filter((word) => word !== ""),
      contexts: editedUserWord.contexts
        .map((context) => ({
          ..._.omit(context, ["preactKey", "valid", "generate"]),
          text: context.text.trim(),
        }))
        .filter((context) => context.text !== ""),
      imageURL: editedUserWord.imageURL ?? "",
      notes: editedUserWord.note ?? "",
    };

    await this.props.userWord.save(
      {
        ...(userWordToSave as any),
      },
      {
        patch: true,
      }
    );

    this.props.savedCallback?.();

    return true;
  }

  renderExplanation(explanation: Explanation | undefined) {
    switch (explanation?.type) {
      case "fetched":
        return <div class="explanation">{explanation.explanation}</div>;

      case "generating":
        return <div class="explanation">Generating...</div>;

      case "reached-limit":
        return (
          <div class="explanation">
            You've reached your limit of 10 explanations per day.
            <button
              class="linkButton upgrade"
              onClick={() => {
                accountModal.show({
                  model: models.user,
                  title: "explanationsLimit",
                  messageHtml:
                    "<div>" +
                    "<h2>Upgrade for unlimited explanations</h2>" +
                    "<p>Really understand what you're learning with unlimited AI-based " +
                    "context-aware explanations!</p>" +
                    "</div>",
                  showTitle: false,
                  smartDefinitions: true,
                });
              }}
            >
              Upgrade to Premium
            </button>{" "}
            for unlimited explanations.
          </div>
        );

      default:
        return null;
    }
  }
}

export default EditCardView;
